import { cn } from "@productlane/lib/src/browser"

import type { AvatarProps } from "./avatar"
import { Avatar } from "./avatar"

const SIZE_CONFIG: Record<
	NonNullable<AvatarProps["size"]>,
	{ marginLeft: string; mask: string }
> = {
	xxs: {
		marginLeft: "-4px",
		mask: "radial-gradient(circle 9px at -4px 50%, transparent 99%, #fff 100%)",
	},
	"xxs+": {
		marginLeft: "-4.5px",
		mask: "radial-gradient(circle 10px at -4.5px 50%, transparent 99%, #fff 100%)",
	},
	xs: {
		marginLeft: "-5px",
		mask: "radial-gradient(circle 11px at -5px 50%, transparent 99%, #fff 100%)",
	},
	s: {
		marginLeft: "-6px",
		mask: "radial-gradient(circle 13.5px at -6px 50%, transparent 99%, #fff 100%)",
	},
	sm: {
		marginLeft: "-8px",
		mask: "radial-gradient(circle 18px at -8px 50%, transparent 99%, #fff 100%)",
	},
	md: {
		marginLeft: "-10px",
		mask: "radial-gradient(circle 22.5px at -10px 50%, transparent 99%, #fff 100%)",
	},
	lg: {
		marginLeft: "-12px",
		mask: "radial-gradient(circle 27px at -12px 50%, transparent 99%, #fff 100%)",
	},
	xl: {
		marginLeft: "-14px",
		mask: "radial-gradient(circle 31.5px at -14px 50%, transparent 99%, #fff 100%)",
	},
}

export interface AvatarStackItem {
	name: string
	image?: string | null
	colorSeed?: string | null
}

export interface AvatarStackProps {
	avatars: AvatarStackItem[]
	size?: AvatarProps["size"]
	slice?: number
	className?: string
	roundness?: AvatarProps["roundness"]
	/** Ring colour drawn between overlapping avatars, e.g. "ring-white dark:ring-gray-900". */
	ringClassName?: string
}

export const AvatarStack = ({
	avatars,
	size = "xxs",
	slice = 3,
	className,
	roundness,
	ringClassName,
}: AvatarStackProps) => {
	const config = SIZE_CONFIG[size ?? "xxs"]
	const visible = avatars.slice(0, slice)

	return (
		<div className={cn("isolate flex items-center", className)}>
			{visible.map((avatar, index) => (
				<Avatar
					key={avatar.image ?? index}
					name={avatar.name}
					image={avatar.image}
					colorSeed={avatar.colorSeed}
					size={size}
					roundness={roundness}
					className={cn(ringClassName && "ring-2", ringClassName)}
					style={{
						// An explicit roundness has to win over the default round crop.
						...(roundness ? {} : { borderRadius: "50%" }),
						zIndex: visible.length - index,
						flexShrink: 0,
						...(index > 0 && {
							marginLeft: config.marginLeft,
							// A circular cut-out on a square avatar bites a notch out of
							// it, so a ring separates the layers instead.
							...(ringClassName ? {} : { mask: config.mask }),
						}),
					}}
				/>
			))}
		</div>
	)
}
